import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 逻辑谜题动画 - 推理步骤展示
 * 
 * 核心效果：
 * 1. 谜题问题展示
 * 2. 线索逐条出现
 * 3. 逻辑推理步骤动画
 * 4. 答案揭晓动画
 */

interface LogicClue {
  text: string;
  icon: string;
}

interface LogicStep {
  premise: string;
  conclusion: string;
  highlight?: string;
}

export const examplePuzzle = {
  question: '谁偷了蛋糕？',
  suspects: ['Alice', 'Bob', 'Charlie', 'Diana'],
  clues: [
    { text: '只有一个人说真话', icon: '🎯' },
    { text: 'Alice 说: Bob 偷的', icon: '👤' },
    { text: 'Bob 说: Diana 偷的', icon: '👤' },
    { text: 'Charlie 说: 我没偷', icon: '🚫' },
    { text: 'Diana 说: Bob 说谎', icon: '⚠️' },
  ],
  steps: [
    {
      premise: '假设 Alice 说真话',
      conclusion: '→ Bob 偷的',
      highlight: 'Bob',
    },
    {
      premise: '则 Bob 说谎',
      conclusion: '→ Diana 没偷',
    },
    {
      premise: 'Charlie 说谎',
      conclusion: '→ Charlie 偷的',
    },
    {
      premise: '矛盾！',
      conclusion: 'Alice 说谎',
      highlight: 'error',
    },
    {
      premise: 'Bob 说谎 → Diana 偷的',
      conclusion: '但 Diana 说 "Bob 说谎" (真话)',
      highlight: 'contradiction',
    },
    {
      premise: '最终答案',
      conclusion: 'Charlie 偷了蛋糕',
      highlight: 'answer',
    },
  ],
};

export const LogicPuzzleAnimation: React.FC<{
  puzzle?: typeof examplePuzzle;
  primaryColor?: string;
  secondaryColor?: string;
  backgroundColor?: string;
}> = ({
  puzzle = examplePuzzle,
  primaryColor = '#FF6B35',
  secondaryColor = '#4ECDC4',
  backgroundColor = '#0F1419',
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // 问题阶段 (0-60 帧)
  const questionOpacity = interpolate(
    frame,
    [0, 30],
    [0, 1],
    { extrapolateRight: 'clamp' }
  );
  const questionScale = spring({
    frame,
    from: 0.8,
    to: 1,
    fps,
    config: { damping: 12, stiffness: 100 },
  });

  // 线索阶段 (60-180 帧)
  const clueStartFrame = 60;
  const cluesVisible = Math.floor(
    interpolate(
      frame,
      [clueStartFrame, clueStartFrame + 120],
      [0, puzzle.clues.length],
      { extrapolateRight: 'clamp', extrapolateLeft: 'clamp' }
    )
  );

  // 推理阶段 (180-420 帧)
  const reasoningStartFrame = 180;
  const stepsVisible = Math.floor(
    interpolate(
      frame,
      [reasoningStartFrame, reasoningStartFrame + 240],
      [0, puzzle.steps.length],
      { extrapolateRight: 'clamp', extrapolateLeft: 'clamp' }
    )
  );

  // 答案阶段 (420-480 帧)
  const answerStartFrame = 420;
  const answerOpacity = interpolate(
    frame,
    [answerStartFrame, answerStartFrame + 30],
    [0, 1],
    { extrapolateRight: 'clamp', extrapolateLeft: 'clamp' }
  );
  
  const answerScale = spring({
    frame: frame - answerStartFrame,
    from: 0.5,
    to: 1,
    fps,
    config: { damping: 10, stiffness: 150 },
  });

  return (
    <AbsoluteFill
      style={{
        background: `linear-gradient(135deg, ${backgroundColor} 0%, #1a1f3a 100%)`,
        fontFamily: 'Arial, sans-serif',
        color: 'white',
        padding: 60,
      }}
    >
      {/* 问题区域 */}
      <div
        style={{
          position: 'absolute',
          top: 60,
          left: 60,
          right: 60,
          textAlign: 'center',
          opacity: questionOpacity,
          transform: `scale(${questionScale})`,
        }}
      >
        <h1
          style={{
            fontSize: 72,
            fontWeight: 'bold',
            margin: 0,
            background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          {puzzle.question}
        </h1>
        
        {/* 嫌疑人列表 */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: 40,
            marginTop: 30,
          }}
        >
          {puzzle.suspects.map((suspect, i) => {
            const suspectOpacity = interpolate(
              frame,
              [30 + i * 5, 30 + i * 5 + 10],
              [0, 1],
              { extrapolateRight: 'clamp' }
            );
            
            return (
              <div
                key={i}
                style={{
                  padding: '15px 30px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: 15,
                  fontSize: 32,
                  opacity: suspectOpacity,
                  border: '2px solid rgba(255, 255, 255, 0.2)',
                }}
              >
                {suspect}
              </div>
            );
          })}
        </div>
      </div>

      {/* 线索区域 */}
      <div
        style={{
          position: 'absolute',
          top: 260,
          left: 80,
          width: 400,
        }}
      >
        <h2
          style={{
            fontSize: 36,
            marginBottom: 20,
            color: secondaryColor,
            opacity: frame > clueStartFrame - 10 ? 1 : 0,
          }}
        >
          📋 线索
        </h2>
        
        {puzzle.clues.slice(0, cluesVisible).map((clue, i) => {
          const clueOpacity = interpolate(
            frame,
            [clueStartFrame + i * 20, clueStartFrame + i * 20 + 10],
            [0, 1],
            { extrapolateRight: 'clamp' }
          );
          
          const clueX = interpolate(
            frame,
            [clueStartFrame + i * 20, clueStartFrame + i * 20 + 10],
            [-30, 0],
            { extrapolateRight: 'clamp' }
          );
          
          return (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: 14,
                padding: '10px 16px',
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: 12,
                opacity: clueOpacity,
                transform: `translateX(${clueX}px)`,
                borderLeft: `4px solid ${secondaryColor}`,
              }}
            >
              <span style={{ fontSize: 28, marginRight: 14 }}>{clue.icon}</span>
              <span style={{ fontSize: 26 }}>{clue.text}</span>
            </div>
          );
        })}
      </div>

      {/* 推理步骤区域 */}
      <div
        style={{
          position: 'absolute',
          top: 260,
          right: 80,
          width: 600,
        }}
      >
        <h2
          style={{
            fontSize: 36,
            marginBottom: 20,
            color: primaryColor,
            opacity: frame > reasoningStartFrame - 10 ? 1 : 0,
          }}
        >
          🧠 逻辑推理
        </h2>
        
        {puzzle.steps.slice(0, stepsVisible).map((step, i) => {
          const stepOpacity = interpolate(
            frame,
            [reasoningStartFrame + i * 30, reasoningStartFrame + i * 30 + 15],
            [0, 1],
            { extrapolateRight: 'clamp' }
          );
          
          const stepY = interpolate(
            frame,
            [reasoningStartFrame + i * 30, reasoningStartFrame + i * 30 + 15],
            [20, 0],
            { extrapolateRight: 'clamp' }
          );
          
          const isError = step.highlight === 'error';
          const isContradiction = step.highlight === 'contradiction';
          const isAnswer = step.highlight === 'answer';
          
          const bgColor = isAnswer
            ? `${primaryColor}33`
            : isError
            ? 'rgba(239, 68, 68, 0.2)'
            : isContradiction
            ? 'rgba(239, 68, 68, 0.15)'
            : 'rgba(255, 255, 255, 0.05)';
          
          const borderColor = isAnswer
            ? primaryColor
            : isError || isContradiction
            ? '#EF4444'
            : 'rgba(255, 255, 255, 0.1)';
          
          return (
            <div
              key={i}
              style={{
                marginBottom: 10,
                padding: '12px 20px',
                background: bgColor,
                borderRadius: 12,
                opacity: stepOpacity,
                transform: `translateY(${stepY}px)`,
                border: `2px solid ${borderColor}`,
              }}
            >
              <div style={{ fontSize: 24, marginBottom: 4, opacity: 0.8 }}>
                {step.premise}
              </div>
              <div style={{ fontSize: 26, fontWeight: 'bold', color: isAnswer ? primaryColor : 'white' }}>
                {step.conclusion}
              </div>
            </div>
          );
        })}
      </div>

      {/* 答案揭晓 */}
      {frame >= answerStartFrame && (
        <div
          style={{
            position: 'absolute',
            top: 360,
            left: 60,
            right: 60,
            textAlign: 'center',
            opacity: answerOpacity,
            transform: `scale(${answerScale})`,
          }}
        >
          <div
            style={{
              padding: '24px 48px',
              background: `linear-gradient(135deg, ${primaryColor}44 0%, ${secondaryColor}44 100%)`,
              borderRadius: 20,
              border: `4px solid ${primaryColor}`,
              display: 'inline-block',
            }}
          >
            <div style={{ fontSize: 28, marginBottom: 10, opacity: 0.8 }}>
              答案揭晓 🎉
            </div>
            <div style={{ fontSize: 42, fontWeight: 'bold' }}>
              {puzzle.steps[puzzle.steps.length - 1].conclusion}
            </div>
          </div>
        </div>
      )}

      {/* 进度指示器 */}
      <div
        style={{
          position: 'absolute',
          bottom: 30,
          left: 60,
          right: 60,
          display: 'flex',
          justifyContent: 'center',
          gap: 15,
        }}
      >
        {['问题', '线索', '推理', '答案'].map((phase, i) => {
          const phaseFrames = [0, 60, 180, 420];
          const isActive = frame >= phaseFrames[i];
          const isCurrent = i < 3 ? frame >= phaseFrames[i] && frame < phaseFrames[i + 1] : frame >= phaseFrames[i];
          
          return (
            <div
              key={i}
              style={{
                padding: '10px 25px',
                background: isActive ? (isCurrent ? primaryColor : `${primaryColor}66`) : 'rgba(255, 255, 255, 0.1)',
                borderRadius: 20,
                fontSize: 24,
                transition: 'all 0.3s',
              }}
            >
              {phase}
            </div>
          );
        })}
      </div>
    </AbsoluteFill>
  );
};
